import { Readiness, FixCategory, Priority } from '../../../types';

export type ReadinessTone =
  | 'strong'
  | 'needs_work'
  | 'significant_gaps'
  | 'unknown';

export interface ReadinessDisplay {
  /** Long copy for the banner headline. */
  label: string;
  /** Short copy for the delta badge ("Needs work → Strong"). */
  short: string;
  /** Drives color theming via a CSS class. */
  tone: ReadinessTone;
}

const READINESS_MAP: Record<Readiness, ReadinessDisplay> = {
  strong: { label: 'Ready to go', short: 'Strong', tone: 'strong' },
  needs_work: {
    label: 'A few improvements recommended',
    short: 'Needs work',
    tone: 'needs_work',
  },
  significant_gaps: {
    label: 'Needs significant work',
    short: 'Significant gaps',
    tone: 'significant_gaps',
  },
};

export function getReadinessDisplay(value: string): ReadinessDisplay {
  return (
    READINESS_MAP[value as Readiness] ?? {
      label: 'Review complete',
      short: 'Reviewed',
      tone: 'unknown',
    }
  );
}

const CATEGORY_LABELS: Record<FixCategory, string> = {
  age_signals: 'Age signals',
  role_fit: 'Role fit',
  impact: 'Impact',
  ats: 'ATS / formatting',
  completeness: 'Completeness',
  market_fit: 'Market fit',
};

export function getCategoryLabel(value: string): string {
  return CATEGORY_LABELS[value as FixCategory] ?? 'Suggestion';
}

const PRIORITY_LABELS: Record<Priority, string> = {
  high: 'High priority',
  medium: 'Medium priority',
  low: 'Low priority',
};

export function getPriorityLabel(value: string): string {
  return PRIORITY_LABELS[value as Priority] ?? 'Suggestion';
}

export type PriorityTone = 'high' | 'medium' | 'low' | 'unknown';

export interface PriorityMeta {
  /** Short visible label for the triage pill ("High" / "Med" / "Low"). */
  short: string;
  /** A leading glyph so priority reads at a glance. */
  symbol: string;
  /** Drives the pill color via a CSS class suffix. */
  tone: PriorityTone;
}

const PRIORITY_META: Record<Priority, PriorityMeta> = {
  high: { short: 'High', symbol: '▲', tone: 'high' },
  medium: { short: 'Med', symbol: '◆', tone: 'medium' },
  low: { short: 'Low', symbol: '•', tone: 'low' },
};

/**
 * Compact triage metadata for a fix's priority. Falls back to a neutral
 * "Note" pill for unknown future priority values rather than crashing.
 * Keep {@link getPriorityLabel} for the full accessible label.
 */
export function getPriorityMeta(value: string): PriorityMeta {
  return (
    PRIORITY_META[value as Priority] ?? { short: 'Note', symbol: '•', tone: 'unknown' }
  );
}

const READINESS_RANK: Record<Readiness, number> = {
  significant_gaps: 0,
  needs_work: 1,
  strong: 2,
};

export type DeltaDirection = 'improved' | 'unchanged' | 'regressed';

/**
 * Compare the current readiness to the readiness from the previous upload.
 * Returns null on the first review (no previous) or when either value is
 * an unknown enum we can't rank.
 */
export function getReadinessDelta(
  previous: string | null,
  current: string
): DeltaDirection | null {
  if (!previous) return null;
  const p = READINESS_RANK[previous as Readiness];
  const c = READINESS_RANK[current as Readiness];
  if (p === undefined || c === undefined) return null;
  if (c > p) return 'improved';
  if (c < p) return 'regressed';
  return 'unchanged';
}
